import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileCheck2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * StudioNudge — surfaces the white-glove 8949 filing product where it's most
 * relevant, so the fulfilment side of the Studio tier doesn't stay hidden.
 *
 *  variant="dashboard"  → Studio users only. CTA → /filings.
 *  variant="tax"        → Non-Studio users only. CTA → /pricing (Studio upsell).
 *
 * Each nudge is dismissible per calendar year (localStorage). We never nag the
 * same user twice for the same tax year. The dismissal key is namespaced by
 * variant so dismissing one place doesn't hide the other.
 */

const filingYear = new Date().getFullYear() - 1; // the tax year currently being filed

const DASH_KEY = `quill.studio-nudge-dash.${filingYear}`;
const TAX_KEY = `quill.studio-nudge-tax.${filingYear}`;

const StudioNudge = ({ variant = "dashboard" }) => {
  const { user } = useAuth();
  const tier = (user?.subscription_tier || "free").toLowerCase();
  const isStudio = tier === "studio";
  const storeKey = variant === "tax" ? TAX_KEY : DASH_KEY;
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(storeKey) === "1"
  );

  // Only render when the audience matches this variant.
  if (variant === "dashboard" && !isStudio) return null;
  if (variant === "tax" && isStudio) return null;
  if (dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(storeKey, "1"); } catch {} // eslint-disable-line no-empty
    setDismissed(true);
  };

  // ── Studio user, on Dashboard: reminder to file TY(current-1)
  if (variant === "dashboard") {
    return (
      <div
        className="quill-card-mint mx-4 md:mx-6 mt-4 md:mt-6 rounded-xl px-4 md:px-6 py-4 flex items-center gap-3 md:gap-4 relative overflow-hidden"
        style={{ color: "hsl(var(--quill-ink-warm))" }}
        data-testid="studio-nudge-dashboard"
      >
        <div className="hidden sm:flex h-9 w-9 items-center justify-center rounded-lg shrink-0"
             style={{ background: "rgba(31, 27, 23, 0.12)" }}>
          <FileCheck2 size={16} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] opacity-70">
            Studio · Concierge
          </div>
          <div className="text-sm md:text-base mt-1 leading-snug">
            Tax season is here — ready to hand off your <b>TY {filingYear}</b> 8949?
            A Quill CPA takes it from Form 8949 to filed with the IRS.
          </div>
        </div>
        <Link
          to="/filings"
          data-testid="studio-nudge-dashboard-cta"
          className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium uppercase tracking-[0.18em] shrink-0 transition-transform hover:-translate-y-0.5"
          style={{ background: "hsl(var(--quill-ink-warm))", color: "#FFF6E5" }}
        >
          Start filing <ArrowUpRight size={12} />
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="studio-nudge-dashboard-dismiss"
          className="ml-1 p-2 -my-1 opacity-60 hover:opacity-100 rounded-md"
          style={{ color: "hsl(var(--quill-ink-warm))" }}
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  // ── Non-Studio user, on Tax: soft upsell to Studio (skip TurboTax)
  return (
    <div
      className="quill-card-cream mx-4 md:mx-6 mt-4 md:mt-6 rounded-xl px-4 md:px-6 py-4 flex items-center gap-3 md:gap-4 relative overflow-hidden"
      style={{ color: "hsl(var(--quill-ink-warm))" }}
      data-testid="studio-nudge-tax"
    >
      <div className="hidden sm:flex h-9 w-9 items-center justify-center rounded-lg shrink-0"
           style={{ background: "rgba(31, 27, 23, 0.12)" }}>
        <FileCheck2 size={16} strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.22em] opacity-70">
          Skip TurboTax
        </div>
        <div className="text-sm md:text-base mt-1 leading-snug">
          Studio ($99/mo) has a real CPA prepare your Form 8949 &amp; Schedule D
          from your wallet activity, then submits to the IRS on your behalf.
        </div>
      </div>
      <Link
        to="/pricing"
        data-testid="studio-nudge-tax-cta"
        className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium uppercase tracking-[0.18em] shrink-0 transition-transform hover:-translate-y-0.5"
        style={{ background: "hsl(var(--quill-ink-warm))", color: "#FFF6E5" }}
      >
        See Studio <ArrowUpRight size={12} />
      </Link>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="studio-nudge-tax-dismiss"
        className="ml-1 p-2 -my-1 opacity-60 hover:opacity-100 rounded-md"
        style={{ color: "hsl(var(--quill-ink-warm))" }}
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default StudioNudge;
